import { forwardRef, useCallback, useRef } from 'react';
import styles from './OrderCardReturn.module.scss';
import {
  WidgetContainer,
  StatusDatePanel,
  ProductImage,
  ExpandedQuantityTab,
  AdditionalInfo,
} from '@chewy/chat-library-components';
import { ReturnCardData } from '@chewy/chat-library-common';
import { KibTruncation, KibTruncationContent, KibTruncationToggle } from '@chewy/kib-truncation-react';
import '@chewy/kib-truncation-styles/dist/kib-truncation.css';
import { first } from 'lodash';
import { useMaxVisibleItems } from './useMaxVisibleItems';

export interface OrderCardReturnProps {
  data: ReturnCardData[];
  isSelectable?: boolean;
  isSelected?: boolean;
  isUnselected?: boolean;
  stackDateStatus?: boolean;
  onSelect?: () => void;
}

const OrderCardReturn = forwardRef<HTMLDivElement, OrderCardReturnProps>(({
  data,
  isSelectable = false,
  isSelected,
  isUnselected,
  stackDateStatus = false,
  onSelect,
}: OrderCardReturnProps, ref) => {
  const containerRef = useRef<HTMLDivElement | null>(null);
  // Return cards use spacing.s1 between thumbnails and keep the previous 4-item cap.
  const MAX_VISIBLE_ITEMS = useMaxVisibleItems(containerRef, {
    initialMax: 4,
    maxItems: 4,
    gapRem: 0.4,
  });
  const returnData = first(data);
  const returnNumber = returnData?.returnNumber ?? '';
  const totalReturns = returnData?.totalReturns ?? 1;
  const orderStatus = returnData?.orderStatus ?? '';
  const dateStatus = returnData?.dateStatus ?? { label: '', status: '', date: '' };
  const items = returnData?.items ?? [];
  const remainingCount = items.length - MAX_VISIBLE_ITEMS;
  // Avoid a "+1" badge; show the final thumbnail in the reserved badge slot instead.
  const showBadge = remainingCount > 1;
  const visibleItems = items.slice(0, showBadge ? MAX_VISIBLE_ITEMS : items.length);
  const additionalMetaData = returnData?.additionalMetaData ?? [];
  const hasAdditionalInfo = additionalMetaData.length > 0;

  const setContainerRef = useCallback((node: HTMLDivElement | null) => {
    containerRef.current = node;
    if (typeof ref === 'function') {
      ref(node);
    } else if (ref) {
      ref.current = node;
    }
  }, [ref]);

  return (
    <WidgetContainer
      isSelectable={isSelectable}
      isSelected={isSelected}
      isUnselected={isUnselected}
      onSelect={onSelect}
      data={data}
    >
      <div ref={setContainerRef} className={styles.returnCard}>
        <StatusDatePanel
          orderStatus={orderStatus}
          dateStatus={dateStatus}
          stackDateStatus={stackDateStatus}
        />

        <KibTruncation>
          <KibTruncationContent
            summary={
              <div className={styles.itemsGrid}>
                {visibleItems.map((item, index) => (
                  <ProductImage
                    key={index}
                    imageUrl={item.imageUrl || ''}
                    name={item.name || 'Return item'}
                    quantity={item.quantity}
                  />
                ))}
                {showBadge && (
                  <ExpandedQuantityTab remainingCount={remainingCount} />
                )}
              </div>
            }
          >
            <div className={`${styles.itemsGrid} ${styles.expanded}`}>
              {items.map((item, index) => (
                <ProductImage
                  key={index}
                  imageUrl={item.imageUrl || ''}
                  name={item.name || 'Return item'}
                  quantity={item.quantity}
                />
              ))}
            </div>
          </KibTruncationContent>
          {showBadge && <KibTruncationToggle className={styles.truncationToggle} labelMore="Show all" labelLess="Show less" />}
        </KibTruncation>

        {hasAdditionalInfo && (
          <div className={styles.additionalInfoContainer}>
            <AdditionalInfo additionalInfo={additionalMetaData} />
          </div>
        )}
        {returnNumber && totalReturns && (
          <div className={styles.returnLabel}>
            <span className={styles.returnNumber}>{`${returnNumber}/${totalReturns}`}</span>
          </div>
        )}
      </div>
    </WidgetContainer>
  );
});

export default OrderCardReturn;
